import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, RefreshCw, Check, Trash2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import { onlyDigits, formatBRL, centsToBRL } from "@/lib/currency";
import { toast } from "sonner";

const TIPOS = ["condominio", "iptu", "seguro", "manutencao", "taxa", "outros"];
const TIPO_LABEL: Record<string, string> = {
  condominio: "Condomínio", iptu: "IPTU", seguro: "Seguro",
  manutencao: "Manutenção", taxa: "Taxa", outros: "Outros",
};

interface Recorrente {
  id: string; imovelId: string; tipo: string; descricao: string | null;
  valorCentavos: number; diaVencimento: number; periodicidade: string;
  ativa: number; dataFim: string | null;
}
interface Esporadica {
  id: string; imovelId: string; recorrenteId: string | null; origem: string;
  tipo: string; descricao: string | null; valorCentavos: number;
  dataVencimento: string; dataPagamento: string | null;
  status: "pendente" | "pago" | "cancelado"; observacao: string | null;
}

const emptyRec = { tipo: "condominio", descricao: "", valorDigitos: "", diaVencimento: "" };
const emptyEsp = { tipo: "manutencao", descricao: "", valorDigitos: "", dataVencimento: "", observacao: "" };

export default function Despesas() {
  const imovelId = window.location.pathname.split("/")[2];
  const [imovelNome, setImovelNome] = useState<string>("");
  const [tab, setTab] = useState<"recorrentes" | "esporadicas">("esporadicas");
  const [loading, setLoading] = useState(true);
  const [recorrentes, setRecorrentes] = useState<Recorrente[]>([]);
  const [esporadicas, setEsporadicas] = useState<Esporadica[]>([]);
  const [formRec, setFormRec] = useState({ ...emptyRec });
  const [formEsp, setFormEsp] = useState({ ...emptyEsp });
  const [saving, setSaving] = useState(false);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (imovelId) carregarTudo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imovelId]);

  const carregarTudo = async () => {
    try {
      setLoading(true);
      const [ri, ei, ii] = await Promise.all([
        fetch(`${API_BASE}/api/imoveis/${imovelId}/despesas/recorrentes`, { credentials: "include" }),
        fetch(`${API_BASE}/api/imoveis/${imovelId}/despesas/esporadicas`, { credentials: "include" }),
        fetch(`${API_BASE}/api/imoveis/${imovelId}`, { credentials: "include" }),
      ]);
      if (ri.ok) setRecorrentes((await ri.json()).recorrentes ?? []);
      if (ei.ok) setEsporadicas((await ei.json()).esporadicas ?? []);
      if (ii.ok) { const d = await ii.json(); setImovelNome(d.imovel?.nome || ""); }
    } catch {
      toast.error("Erro ao carregar despesas");
    } finally {
      setLoading(false);
    }
  };

  const salvarRecorrente = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(Number(formRec.diaVencimento) >= 1 && Number(formRec.diaVencimento) <= 31)) {
      toast.error("Dia de vencimento deve ser entre 1 e 31"); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/imoveis/${imovelId}/despesas/recorrentes`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: formRec.tipo,
          descricao: formRec.descricao || null,
          valorCentavos: Number(formRec.valorDigitos || "0"),
          diaVencimento: Number(formRec.diaVencimento),
          periodicidade: "mensal",
        }),
      });
      if (res.ok) {
        toast.success("Despesa recorrente criada");
        setFormRec({ ...emptyRec });
        carregarTudo();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Erro ao salvar");
      }
    } finally { setSaving(false); }
  };

  const salvarEsporadica = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/imoveis/${imovelId}/despesas/esporadicas`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: formEsp.tipo,
          descricao: formEsp.descricao || null,
          valorCentavos: Number(formEsp.valorDigitos || "0"),
          dataVencimento: formEsp.dataVencimento,
          observacao: formEsp.observacao || null,
        }),
      });
      if (res.ok) {
        toast.success("Despesa registrada");
        setFormEsp({ ...emptyEsp });
        carregarTudo();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Erro ao salvar");
      }
    } finally { setSaving(false); }
  };

  const [totalParcelas, setTotalParcelas] = useState(1);

  const gerarParcelas = async () => {
    setGerando(true);
    try {
      const res = await fetch(`${API_BASE}/api/despesas/recorrentes/gerar`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ totalParcelas: Number(totalParcelas) }),
      });
      if (res.ok) {
        const d = await res.json();
        const preview = d.totalParcelas > 1 ? `Vence até ${new Date(d.dataFim).toLocaleDateString("pt-BR")}` : undefined;
        toast.success(d.geradas > 0 ? `${d.geradas} parcela(s) gerada(s) para ${d.mes}` + (preview ? ` — ${preview}` : "") : "Nenhuma parcela nova a gerar");
        carregarTudo();
      } else { toast.error("Erro ao gerar parcelas"); }
    } finally { setGerando(false); }
  };

  const marcarPago = async (d: Esporadica) => {
    const res = await fetch(`${API_BASE}/api/despesas/esporadicas/${d.id}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: d.status === "pago" ? "pendente" : "pago" }),
    });
    if (res.ok) { toast.success(d.status === "pago" ? "Marcada como pendente" : "Marcada como paga"); carregarTudo(); }
    else toast.error("Não foi possível atualizar");
  };

  const excluirEsp = async (d: Esporadica) => {
    const res = await fetch(`${API_BASE}/api/despesas/esporadicas/${d.id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success(res.status === 204 ? "Excluída" : "Parcela recorrente cancelada"); carregarTudo(); }
    else toast.error("Não foi possível excluir");
  };

  const excluirRec = async (r: Recorrente) => {
    const res = await fetch(`${API_BASE}/api/despesas/recorrentes/${r.id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Despesa recorrente excluída"); setRecorrentes((p) => p.filter((x) => x.id !== r.id)); }
    else toast.error("Não foi possível excluir");
  };

  const fmtData = (iso: string) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "-");

  if (loading) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{imovelNome || "Despesas do Imóvel"}</h1>
            <p className="text-sm text-muted-foreground">Despesas recorrentes e esporádicas</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <button
            className={cn("px-4 py-2 rounded-lg text-sm font-medium border",
              tab === "esporadicas" ? "bg-primary text-on-primary border-primary" : "bg-card text-muted-foreground")}
            onClick={() => setTab("esporadicas")}
          >
            Esporádicas ({esporadicas.length})
          </button>
          <button
            className={cn("px-4 py-2 rounded-lg text-sm font-medium border",
              tab === "recorrentes" ? "bg-primary text-on-primary border-primary" : "bg-card text-muted-foreground")}
            onClick={() => setTab("recorrentes")}
          >
            Recorrentes ({recorrentes.length})
          </button>
        </div>

        {tab === "esporadicas" && (
          <>
            <Card className="mb-6">
              <CardHeader><CardTitle>Nova Despesa</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={salvarEsporadica} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <div className="space-y-1">
                    <Label>Tipo *</Label>
                    <Select value={formEsp.tipo} onValueChange={(v) => setFormEsp({ ...formEsp, tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label htmlFor="espDesc">Descrição</Label>
                    <Input id="espDesc" value={formEsp.descricao} onChange={(e) => setFormEsp({ ...formEsp, descricao: e.target.value })} placeholder="Ex: pintura da fachada" />
                  </div>
                  <div className="space-y-1">
                    <Label>Valor (R$) *</Label>
                    <Input type="text" inputMode="numeric" required
                      value={formatBRL(formEsp.valorDigitos)}
                      onChange={(e) => setFormEsp({ ...formEsp, valorDigitos: onlyDigits(e.target.value) })}
                      placeholder="0,00" />
                  </div>
                  <div className="space-y-1">
                    <Label>Vencimento *</Label>
                    <Input type="date" required value={formEsp.dataVencimento}
                      onChange={(e) => setFormEsp({ ...formEsp, dataVencimento: e.target.value })} />
                  </div>
                  <div className="md:col-span-5 flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                      Adicionar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                {esporadicas.length === 0 ? (
                  <p className="text-center text-muted-foreground py-10">Nenhuma despesa esporádica registrada.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          {["Tipo", "Descrição", "Valor", "Vencimento", "Status", "Ações"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {esporadicas.map((d) => (
                          <tr key={d.id} className="hover:bg-muted/50">
                            <td className="px-4 py-3">{TIPO_LABEL[d.tipo] || d.tipo}{d.origem === "recorrente" && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">rec.</span>}</td>
                            <td className="px-4 py-3">{d.descricao || "-"}</td>
                            <td className="px-4 py-3 font-medium">{centsToBRL(d.valorCentavos)}</td>
                            <td className="px-4 py-3">{fmtData(d.dataVencimento)}</td>
                            <td className="px-4 py-3">
                              <span className={cn("px-2 py-1 rounded-full text-xs font-medium",
                                d.status === "pago" ? "bg-green-100 text-green-800" :
                                d.status === "cancelado" ? "bg-gray-100 text-gray-600" :
                                "bg-yellow-100 text-yellow-800")}>
                                {d.status === "pago" ? "Paga" : d.status === "cancelado" ? "Cancelada" : "Pendente"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                {d.status !== "cancelado" && (
                                  <Button variant="ghost" size="icon" title={d.status === "pago" ? "Marcar pendente" : "Marcar paga"} onClick={() => marcarPago(d)}>
                                    <Check className={cn("h-4 w-4", d.status === "pago" ? "text-muted-foreground" : "text-green-600")} />
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" title="Excluir/Cancelar" onClick={() => excluirEsp(d)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {tab === "recorrentes" && (
          <>
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Nova Recorrente Mensal</CardTitle>
                <Button variant="outline" size="sm" onClick={gerarParcelas} disabled={gerando}>
                  {gerando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Gerar parcelas do mês
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={salvarRecorrente} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <div className="space-y-1">
                    <Label>Tipo *</Label>
                    <Select value={formRec.tipo} onValueChange={(v) => setFormRec({ ...formRec, tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label htmlFor="recDesc">Descrição</Label>
                    <Input id="recDesc" value={formRec.descricao} onChange={(e) => setFormRec({ ...formRec, descricao: e.target.value })} placeholder="Ex: taxa de lixo" />
                  </div>
                  <div className="space-y-1">
                    <Label>Valor (R$) *</Label>
                    <Input type="text" inputMode="numeric" required
                      value={formatBRL(formRec.valorDigitos)}
                      onChange={(e) => setFormRec({ ...formRec, valorDigitos: onlyDigits(e.target.value) })}
                      placeholder="0,00" />
                  </div>
                  <div className="space-y-1">
                    <Label>Dia venc. (1–31) *</Label>
                    <Input type="number" min={1} max={31} required value={formRec.diaVencimento}
                      onChange={(e) => setFormRec({ ...formRec, diaVencimento: e.target.value })} placeholder="10" />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label>Total de parcelas</Label>
                    <Input type="number" min={1} value={totalParcelas}
                      onChange={(e) => setTotalParcelas(Number(e.target.value))} placeholder="1" />
                    <small className="text-muted-foreground">Deixe em 1 para gerar 1 parcela/mês</small>
                  </div>
                  <div className="md:col-span-5 flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                      Adicionar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                {recorrentes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-10">
                    Nenhuma despesa recorrente. Cadastre e use "Gerar parcelas do mês".
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          {["Tipo", "Descrição", "Valor", "Dia venc.", "Status", "Ações"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {recorrentes.map((r) => (
                          <tr key={r.id} className="hover:bg-muted/50">
                            <td className="px-4 py-3">{TIPO_LABEL[r.tipo] || r.tipo}</td>
                            <td className="px-4 py-3">{r.descricao || "-"}</td>
                            <td className="px-4 py-3 font-medium">{centsToBRL(r.valorCentavos)}</td>
                            <td className="px-4 py-3">dia {r.diaVencimento}</td>
                            <td className="px-4 py-3">
                              <span className={cn("px-2 py-1 rounded-full text-xs font-medium",
                                r.ativa ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600")}>
                                {r.ativa ? "Ativa" : "Inativa"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="icon" title="Excluir" onClick={() => excluirRec(r)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
